'use client';

import { styled, css } from 'styled-components';
import cs from 'classnames';

type TagProps = {
  /** Tag key from `tagLibrary`. Blog category tags use `BlogCategoryTag` instead. */
  tag: TagKey;

  /**
   * Visual treatment.
   * - `default`: pill uses the data's colors.
   * - `subtle`: white bg with grey700 text; the data's colors return on hover.
   */
  variant?: 'default' | 'subtle';

  /** Optional class name to apply to the tag and allow custom styling */
  className?: string;
};

/**
 * Utility type to filter tag keys by their type property
 * Usage: TagsByType<'category'> | TagsByType<'feature'> | TagsByType<'pricing'>
 */
type TagsByType<T extends string> = {
  [
    TagKey in keyof typeof tagLibrary
  ]: (typeof tagLibrary)[TagKey]['type'] extends T ? TagKey : never;
}[keyof typeof tagLibrary];

/**
 * Export types for tag filtering scenarios
 *
 * // Use only feature tags
 * tags: FeatureTags[]
 *
 * // Multiple combination of tags
 * tags: (CategoryTags | PricingTags)[]
 */
export type CategoryTags = TagsByType<'category'>;
export type FeatureTags = TagsByType<'feature'>;
export type PricingTags = TagsByType<'pricing'>;

export type TagKey = keyof typeof tagLibrary;

/**
 * Product tags (categories, features, pricing). Blog categories are a separate
 * registry (`@lib/data/blogCategories`) rendered by `BlogCategoryTag`; they are
 * intentionally not merged here so a shared key (e.g. `webinars`) can't collide.
 */
export const tagLibrary = {
  // Categories
  advertising: {
    name: 'Advertising',
    textColor: 'pink800',
    backgroundColor: 'pink200',
    type: 'category',
  },
  analytics: {
    name: 'Analytics',
    textColor: 'pink800',
    backgroundColor: 'pink300',
    type: 'category',
  },
  content_management: {
    name: 'Content Management',
    textColor: 'orange800',
    backgroundColor: 'orange200',
    type: 'category',
  },
  email_marketing: {
    name: 'Email Marketing',
    textColor: 'green800',
    backgroundColor: 'green300',
    type: 'category',
  },
  marketing_automation: {
    name: 'Marketing Automation',
    textColor: 'blue800',
    backgroundColor: 'blue200',
    type: 'category',
  },
  production: {
    name: 'Production',
    textColor: 'blue800',
    backgroundColor: 'blue300',
    type: 'category',
  },
  productivity: {
    name: 'Productivity',
    textColor: 'blue800',
    backgroundColor: 'yellow400',
    type: 'category',
  },
  sales: {
    name: 'Sales',
    textColor: 'green800',
    backgroundColor: 'green400',
    type: 'category',
  },
  social_media: {
    name: 'Social Media',
    textColor: 'purple800',
    backgroundColor: 'purple300',
    type: 'category',
  },
  support: {
    name: 'Support',
    textColor: 'purple800',
    backgroundColor: 'purple200',
    type: 'category',
  },
  // Features
  webinars: {
    name: 'Webinars',
    textColor: 'pink800',
    backgroundColor: 'pink300',
    type: 'feature',
  },
  video: {
    name: 'Video',
    textColor: 'green800',
    backgroundColor: 'green300',
    type: 'feature',
  },
  channels: {
    name: 'Channels',
    textColor: 'blue800',
    backgroundColor: 'blue200',
    type: 'feature',
  },
  custom: {
    name: 'Custom',
    textColor: 'yellow800',
    backgroundColor: 'yellow200',
    type: 'feature',
  },
  // Pricing
  advanced: {
    name: 'Advanced',
    textColor: 'blue800',
    backgroundColor: 'blue100',
    type: 'pricing',
  },
  premium: {
    name: 'Premium',
    textColor: 'blue800',
    backgroundColor: 'blue100',
    type: 'pricing',
  },
  pro: {
    name: 'Pro',
    textColor: 'blue800',
    backgroundColor: 'blue100',
    type: 'pricing',
  },
  free: {
    name: 'Free',
    textColor: 'blue800',
    backgroundColor: 'blue100',
    type: 'pricing',
  },
} as const;

export type TagData = {
  name: string;
  textColor: string;
  backgroundColor: string;
};

/**
 * Helper functions to create filtered tag objects by type
 */
const createCategoryTags = () =>
  Object.fromEntries(
    Object.entries(tagLibrary).filter(([, tag]) => tag.type === 'category'),
  ) as Pick<typeof tagLibrary, CategoryTags>;

const createFeatureTags = () =>
  Object.fromEntries(
    Object.entries(tagLibrary).filter(([, tag]) => tag.type === 'feature'),
  ) as Pick<typeof tagLibrary, FeatureTags>;

const createPricingTags = () =>
  Object.fromEntries(
    Object.entries(tagLibrary).filter(([, tag]) => tag.type === 'pricing'),
  ) as Pick<typeof tagLibrary, PricingTags>;

/**
 * Static filtered tagLibrary objects - computed once at build time
 */
export const categoryTagLibrary = createCategoryTags();
export const featureTagLibrary = createFeatureTags();
export const pricingTagLibrary = createPricingTags();

export const tagStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: max-content;
  white-space: nowrap;
  padding-block: ${({ theme }) => theme.spacing[0.25]};
  padding-inline: ${({ theme }) => theme.spacing[0.75]};
  border-radius: ${({ theme }) => theme.radius.btn};
  font-size: ${({ theme }) => theme.typography.fontSize.bodyXs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.headingTwo};
`;

export const TagWrapper = styled.span<{
  $textColor: string;
  $backgroundColor: string;
}>`
  ${tagStyles}
  gap: ${({ theme }) => theme.spacing[0.25]};
  background-color: ${({ theme, $backgroundColor }) =>
    theme.colors[$backgroundColor]};
  color: ${({ theme, $textColor }) => theme.colors[$textColor]};

  &.subtle {
    background-color: ${({ theme }) => theme.colors.white};
    color: ${({ theme }) => theme.colors.grey700};
    transition:
      background-color var(--global-motion),
      color var(--global-motion);
  }

  &.subtle:hover {
    background-color: ${({ theme, $backgroundColor }) =>
      theme.colors[$backgroundColor]};
    color: ${({ theme, $textColor }) => theme.colors[$textColor]};
  }
`;

/**
 * Tags categorize content. Use `tagLibrary` keys for product tags; blog
 * category tags are rendered by `BlogCategoryTag` (`@dvd/blog/components`).
 */
const Tag = ({ tag, variant = 'default', className }: TagProps) => {
  const tagData = tagLibrary[tag];

  return (
    <TagWrapper
      className={cs(className, variant)}
      $textColor={tagData.textColor}
      $backgroundColor={tagData.backgroundColor}
    >
      {tagData.name}
    </TagWrapper>
  );
};

export default Tag;
